// Nom et version de la base de données
const ENV_PREFIX = process.env.REACT_APP_ENV || 'development';
const DB_NAME = `${ENV_PREFIX}_VerbatimLabelerDB`;
const DB_VERSION = 1;

// Fonction pour initialiser la base de données
const initDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = (event) => {
      reject(`Erreur d'ouverture de la base de données: ${event.target.error}`);
    };

    request.onsuccess = (event) => {
      resolve(event.target.result);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      // Créer les object stores avec les keyPath appropriés
      // Chaque store correspond à une clé précédemment utilisée dans localStorage
      if (!db.objectStoreNames.contains('storedVerbatims')) {
        db.createObjectStore('storedVerbatims', { keyPath: 'id' });
      }
      
      if (!db.objectStoreNames.contains('notes')) {
        db.createObjectStore('notes', { keyPath: 'id' });
      }
      
      if (!db.objectStoreNames.contains('slides')) {
        db.createObjectStore('slides', { keyPath: 'id' });
      }
      
      if (!db.objectStoreNames.contains('setupData')) {
        // Pour setupData, utiliser 'id' comme keyPath mais avec une valeur fixe 'config'
        db.createObjectStore('setupData', { keyPath: 'id' });
      }
      
      // Pour les données simples sans ID naturel
      if (!db.objectStoreNames.contains('appSettings')) {
        db.createObjectStore('appSettings', { keyPath: 'key' });
      }
    };
  });
};

// Sauvegarder des données dans IndexedDB
export const saveToIndexedDB = async (storeName, data, key = null) => {
  try {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      
      let request;
      
      // Pour les données de paramètres (comme enableAutosave, intervalNumber, etc.)
      if (storeName === 'appSettings') {
        request = store.put({ key, value: data });
      } 
      // Pour les collections (verbatims, notes, slides)
      else if (Array.isArray(data)) {
        // Vider le store avant d'ajouter toutes les données
        store.clear();
        
        // Ajouter chaque élément individuellement
        const putPromises = data.map(item => {
          // Vérifier si l'élément a la propriété id
          if (!item.id && item.id !== 0) {
            console.warn(`Élément sans id détecté dans ${storeName}, ajout d'un id générique`);
            item = { ...item, id: Date.now() + Math.random() };
          }
          return store.put(item);
        });
        
        request = transaction;
      } 
      // Pour setupData qui n'a pas de keyPath naturel
      else if (storeName === 'setupData') {
        // Utiliser un identifiant fixe pour les données de configuration
        request = store.put({ id: 'config', ...data });
      }
      // Pour les objets individuels avec ID (autres cas)
      else {
        // Vérifier et ajouter une clé si nécessaire
        if (!data.id && data.id !== 0 && store.keyPath) {
          console.warn(`Données sans propriété id (${store.keyPath}) détectées dans ${storeName}, ajout d'un id générique`);
          data = { ...data, id: Date.now() + Math.random() };
        }
        request = store.put(data);
      }
      
      transaction.oncomplete = () => resolve(true);
      transaction.onerror = (event) => {
        console.error(`Erreur de transaction IndexedDB dans ${storeName}:`, event.target.error);
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error('Erreur de sauvegarde dans IndexedDB:', error);
    // Gérer l'erreur comme dans localStorage - télécharger en JSON si nécessaire
    alert(`Erreur de sauvegarde: ${error.message}. Saving data as a JSON file instead.`);
    
    // Code pour sauvegarder en JSON file
    const json = JSON.stringify(data);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${storeName}-backup.json`;
    a.click();
    URL.revokeObjectURL(url);
  }
};

// Charger des données depuis IndexedDB
export const loadFromIndexedDB = async (storeName, key = null) => {
  try {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      
      let request;
      
      // Pour les données de paramètres
      if (storeName === 'appSettings' && key) {
        request = store.get(key);
        request.onsuccess = (event) => {
          const result = event.target.result;
          resolve(result ? result.value : null);
        };
      } 
      // Pour setupData qui utilise une clé fixe
      else if (storeName === 'setupData') {
        request = store.get('config');
        request.onsuccess = (event) => {
          const result = event.target.result;
          if (result) {
            // Supprimer la propriété id pour rester compatible avec le code existant
            const { id, ...data } = result;
            resolve(data);
          } else {
            resolve(null);
          }
        };
      }
      // Pour les collections (verbatims, notes, slides)
      else {
        request = store.getAll();
        request.onsuccess = (event) => {
          // Assurer que le résultat est un tableau
          const result = event.target.result;
          
          // Si c'est storedVerbatims et que c'est un objet non-tableau, le convertir en tableau
          if (storeName === 'storedVerbatims' && result && !Array.isArray(result)) {
            console.warn('storedVerbatims n\'est pas un tableau, conversion en tableau');
            
            // Si c'est un objet avec des propriétés, convertir en tableau d'objets
            if (typeof result === 'object') {
              const arrayResult = Object.values(result).filter(item => item !== null);
              resolve(arrayResult);
            } else {
              // Sinon, retourner un tableau vide
              resolve([]);
            }
          } else {
            // Pour tout autre cas, s'assurer que c'est un tableau
            resolve(Array.isArray(result) ? result : []);
          }
        };
      }
      
      request.onerror = (event) => {
        console.error(`Erreur de lecture IndexedDB dans ${storeName}:`, event.target.error);
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error('Erreur de chargement depuis IndexedDB:', error);
    // En cas d'erreur, retourner un tableau vide pour les collections
    if (['storedVerbatims', 'notes', 'slides'].includes(storeName)) {
      return [];
    }
    return null;
  }
};

// Supprimer des données depuis IndexedDB
export const removeFromIndexedDB = async (storeName, key = null) => {
  try {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      
      let request;
      
      if (key) {
        request = store.delete(key);
      } else {
        request = store.clear();
      }
      
      transaction.oncomplete = () => resolve(true);
      transaction.onerror = (event) => reject(event.target.error);
    });
  } catch (error) {
    console.error('Erreur de suppression dans IndexedDB:', error);
    return false;
  }
};

// Effacer toutes les données (équivalent à localStorage.clear())
export const clearIndexedDB = async () => {
  try {
    const db = await initDB();
    const storeNames = Array.from(db.objectStoreNames);
    
    return Promise.all(storeNames.map(storeName => 
      removeFromIndexedDB(storeName)
    ));
  } catch (error) {
    console.error('Erreur lors de la suppression des données:', error);
    return false;
  }
};

// Fonction pour migrer les données de localStorage vers IndexedDB
export const migrateFromLocalStorage = async () => {
  // Vérifier si la migration a déjà été effectuée
  const migrated = await loadFromIndexedDB('appSettings', 'migrationCompleted');
  if (migrated) return;

  try {
    // Migrer les verbatims
    const storedVerbatims = JSON.parse(localStorage.getItem('storedVerbatims'));
    if (storedVerbatims) {
      // Vérifier que chaque verbatim a un id
      const validVerbatims = storedVerbatims.map((v, index) => {
        if (!v.id && v.id !== 0) {
          console.warn(`Verbatim sans id détecté pendant la migration, ajout d'un id générique: ${index + 1}`);
          return { ...v, id: index + 1 };
        }
        return v;
      });
      await saveToIndexedDB('storedVerbatims', validVerbatims);
    }

    // Migrer les notes
    const notes = JSON.parse(localStorage.getItem('notes'));
    if (notes) {
      // Vérifier que chaque note a un id
      const validNotes = notes.map((n, index) => {
        if (!n.id && n.id !== 0) {
          console.warn(`Note sans id détectée pendant la migration, ajout d'un id générique: ${index + 1}`);
          return { ...n, id: index + 1 };
        }
        return n;
      });
      await saveToIndexedDB('notes', validNotes);
    }

    // Migrer les slides
    const slides = JSON.parse(localStorage.getItem('slides'));
    if (slides) {
      // Vérifier que chaque slide a un id
      const validSlides = slides.map((s, index) => {
        if (!s.id && s.id !== 0) {
          console.warn(`Slide sans id détecté pendant la migration, ajout d'un id générique: ${index + 1}`);
          return { ...s, id: index + 1 };
        }
        return s;
      });
      await saveToIndexedDB('slides', validSlides);
    }

    // Migrer setupData
    const setupData = JSON.parse(localStorage.getItem('setupData'));
    if (setupData) {
      // setupData est un objet qui recevra automatiquement un id: 'config'
      await saveToIndexedDB('setupData', setupData);
    }

    // Migrer les paramètres d'application
    const settings = ['enableAutosave', 'autosaveInterval', 'intervalNumber', 'dataChanged', 'filters', 'collapsedSections'];
    for (const key of settings) {
      try {
        const value = JSON.parse(localStorage.getItem(key));
        if (value !== null) {
          await saveToIndexedDB('appSettings', value, key);
        }
      } catch (error) {
        console.warn(`Erreur lors de la migration du paramètre ${key}:`, error);
      }
    }

    // Marquer la migration comme complète
    await saveToIndexedDB('appSettings', true, 'migrationCompleted');
    
    console.log('Migration depuis localStorage terminée avec succès');
  } catch (error) {
    console.error('Erreur lors de la migration depuis localStorage:', error);
  }
};

// Fonction pour initialiser les paramètres par défaut si nécessaire
export const initializeDefaultSettings = async () => {
  try {
    console.log('Vérification des paramètres d\'autosauvegarde...');
    
    // Vérifier si les paramètres existent déjà
    const enableAutosave = await loadFromIndexedDB('appSettings', 'enableAutosave');
    const autosaveInterval = await loadFromIndexedDB('appSettings', 'autosaveInterval');
    
    // Initialiser les valeurs par défaut si elles n'existent pas ou sont invalides
    if (enableAutosave === null || enableAutosave === undefined || typeof enableAutosave !== 'boolean') {
      console.log('Initialisation de enableAutosave avec la valeur par défaut: true');
      await saveToIndexedDB('appSettings', true, 'enableAutosave');
    } else {
      console.log('Le paramètre enableAutosave existe déjà:', enableAutosave);
    }
    
    if (autosaveInterval === null || autosaveInterval === undefined || typeof autosaveInterval !== 'number' || autosaveInterval <= 0) {
      console.log('Initialisation de autosaveInterval avec la valeur par défaut: 15');
      await saveToIndexedDB('appSettings', 15, 'autosaveInterval');
    } else {
      console.log('Le paramètre autosaveInterval existe déjà:', autosaveInterval);
    }

    // Marquer dataChanged comme false si non défini
    const dataChanged = await loadFromIndexedDB('appSettings', 'dataChanged');
    if (dataChanged === null || dataChanged === undefined) {
      await saveToIndexedDB('appSettings', false, 'dataChanged');
    }

    // S'assurer que le flag de migration est défini pour éviter des migrations répétées
    const migrationCompleted = await loadFromIndexedDB('appSettings', 'migrationCompleted');
    if (migrationCompleted === null || migrationCompleted === undefined) {
      await saveToIndexedDB('appSettings', true, 'migrationCompleted');
    }

    console.log('Initialisation des paramètres par défaut terminée');
  } catch (error) {
    console.error('Erreur lors de l\'initialisation des paramètres par défaut:', error);
    
    // En cas d'erreur, faire une dernière tentative d'initialisation
    try {
      await saveToIndexedDB('appSettings', true, 'enableAutosave');
      await saveToIndexedDB('appSettings', 15, 'autosaveInterval');
      await saveToIndexedDB('appSettings', false, 'dataChanged');
      await saveToIndexedDB('appSettings', true, 'migrationCompleted');
    } catch (finalError) {
      console.error('Échec de la tentative finale d\'initialisation des paramètres par défaut:', finalError);
    }
  }
}; 